import { useCallback, useMemo, useState } from 'react';
import { Senario, SpilloverPriority } from 'src/entities/planningEntity';

/**
 * `PlanningPresenter` の設定 state。移設元は ConvenienceDispatches/Presenter.tsx の
 * `planningDialogSettings`（旧 :2308-2329）。20 キー。
 *
 * `relaxedRules` / `selectedRelaxedRules` / `priorityLargeTrucks` はここに含めない
 * （`PlanningPresenter` が `setSelectedRelaxedRules` を直接受け取る形になっており、
 * 移設元でも `planningDialogSettings` の外に置かれているため。形を変えない）。
 */
export type PlanningDialogSettings = {
  balancedLoading: boolean;
  excludeToll: boolean;
  includeFutureDeliveries: boolean;
  autoSplitOrders: boolean;
  dontExchangePreroute: boolean;
  concurrentAllOrNothing: boolean;
  expandWorkingStartMinutes: number;
  expandWorkingEndMinutes: number;
  expandLoadStartMinutes: number;
  expandLoadEndMinutes: number;
  expandUnloadStartMinutes: number;
  expandUnloadEndMinutes: number;
  expandMaxVolumeRate: number;
  expandMaxLoadCapacityRate: number;
  activateIncrementalMaxNumPistons: boolean;
  traceMode: boolean;
  traceOnly: boolean;
  traceCompanyId: number;
  senario: Senario;
  disableGlobalOpt: boolean;
  spilloverPriority?: SpilloverPriority;
};

const initialSettings: PlanningDialogSettings = {
  balancedLoading: false,
  excludeToll: false,
  includeFutureDeliveries: false,
  autoSplitOrders: false,
  dontExchangePreroute: true,
  concurrentAllOrNothing: false,
  expandWorkingStartMinutes: 0,
  expandWorkingEndMinutes: 0,
  expandLoadStartMinutes: 0,
  expandLoadEndMinutes: 0,
  expandUnloadStartMinutes: 0,
  expandUnloadEndMinutes: 0,
  expandMaxVolumeRate: 0,
  expandMaxLoadCapacityRate: 0,
  activateIncrementalMaxNumPistons: false,
  traceMode: false,
  traceOnly: false,
  traceCompanyId: 0,
  senario: 'setcover',
  disableGlobalOpt: false,
  spilloverPriority: undefined,
};

/**
 * `PlanningPresenter` にそのまま渡す updater 群。21 キーのうち 19 本
 * （`traceMode` / `traceOnly` は `PlanningPresenter` に対応する `update*` Props が無く、
 * 常に既定値のまま `PlanningEntity` へ入る。移設元も同じ）。
 */
export type PlanningSettingProps = {
  updateBalancedLoading: (v: boolean) => void;
  updateExcludeToll: (v: boolean) => void;
  updateIncludeFutureDeliveries: (v: boolean) => void;
  updateAutoSplitOrders: (v: boolean) => void;
  updateDontExchangePreroute: (v: boolean) => void;
  updateConcurrentAllOrNothing: (v: boolean) => void;
  updateExpandWorkingStartMinutes: (v: number) => void;
  updateExpandWorkingEndMinutes: (v: number) => void;
  updateExpandLoadStartMinutes: (v: number) => void;
  updateExpandLoadEndMinutes: (v: number) => void;
  updateExpandUnloadStartMinutes: (v: number) => void;
  updateExpandUnloadEndMinutes: (v: number) => void;
  updateExpandMaxVolumeRate: (v: number) => void;
  updateExpandMaxLoadCapacityRate: (v: number) => void;
  updateActivateIncrementalMaxNumPistons: (v: boolean) => void;
  updateTraceCompanyId: (v: number) => void;
  updateSenario: (v: Senario) => void;
  updateSpilloverPriority: (v: SpilloverPriority | undefined) => void;
  updateDisableGlobalOpt: (v: boolean) => void;
};

/**
 * `PlanningPresenter` の設定 state と updater をまとめた共有 hook（Task 6-a）。
 * カード配車と `/map`（Task 6-b）が共通で使う。
 *
 * updater は `useMemo` で包み、rerender や他フィールドの setState を挟んでも参照を
 * 変えない。`PlanningPresenter` がこれらを `useEffect` の deps に持ち、その中から
 * 親 state を更新する構造（`PlanningPresenter.tsx:295` ほか）のため、毎 render 新しい
 * 関数を返すと effect の再実行 → 親 state 更新 → 再 render が連鎖してしまう。
 */
export function usePlanningDialogSettings(): {
  settings: PlanningDialogSettings;
  presenterProps: PlanningSettingProps;
} {
  const [settings, setSettings] = useState<PlanningDialogSettings>(initialSettings);

  const makeBoolUpdater = useCallback(
    (key: keyof PlanningDialogSettings) => (v: boolean) => {
      setSettings((s) => ({ ...s, [key]: v }));
    },
    [],
  );
  const makeNumUpdater = useCallback(
    (key: keyof PlanningDialogSettings) => (v: number) => {
      setSettings((s) => ({ ...s, [key]: v }));
    },
    [],
  );

  const updateBalancedLoading = useMemo(() => makeBoolUpdater('balancedLoading'), [makeBoolUpdater]);
  const updateExcludeToll = useMemo(() => makeBoolUpdater('excludeToll'), [makeBoolUpdater]);
  const updateIncludeFutureDeliveries = useMemo(() => makeBoolUpdater('includeFutureDeliveries'), [makeBoolUpdater]);
  const updateAutoSplitOrders = useMemo(() => makeBoolUpdater('autoSplitOrders'), [makeBoolUpdater]);
  const updateDontExchangePreroute = useMemo(() => makeBoolUpdater('dontExchangePreroute'), [makeBoolUpdater]);
  const updateConcurrentAllOrNothing = useMemo(() => makeBoolUpdater('concurrentAllOrNothing'), [makeBoolUpdater]);
  const updateExpandWorkingStartMinutes = useMemo(() => makeNumUpdater('expandWorkingStartMinutes'), [makeNumUpdater]);
  const updateExpandWorkingEndMinutes = useMemo(() => makeNumUpdater('expandWorkingEndMinutes'), [makeNumUpdater]);
  const updateExpandLoadStartMinutes = useMemo(() => makeNumUpdater('expandLoadStartMinutes'), [makeNumUpdater]);
  const updateExpandLoadEndMinutes = useMemo(() => makeNumUpdater('expandLoadEndMinutes'), [makeNumUpdater]);
  const updateExpandUnloadStartMinutes = useMemo(() => makeNumUpdater('expandUnloadStartMinutes'), [makeNumUpdater]);
  const updateExpandUnloadEndMinutes = useMemo(() => makeNumUpdater('expandUnloadEndMinutes'), [makeNumUpdater]);
  const updateExpandMaxVolumeRate = useMemo(() => makeNumUpdater('expandMaxVolumeRate'), [makeNumUpdater]);
  const updateExpandMaxLoadCapacityRate = useMemo(() => makeNumUpdater('expandMaxLoadCapacityRate'), [makeNumUpdater]);
  const updateActivateIncrementalMaxNumPistons = useMemo(
    () => makeBoolUpdater('activateIncrementalMaxNumPistons'),
    [makeBoolUpdater],
  );
  const updateTraceCompanyId = useMemo(() => makeNumUpdater('traceCompanyId'), [makeNumUpdater]);
  // senario は boolean/number どちらの factory にも当てはまらない (Senario は文字列 union)。
  // 移設元も専用の useCallback で個別対応しており、ここでも同じ形にする
  const updateSpilloverPriority = useCallback(
    (v: SpilloverPriority | undefined) => setSettings((s) => ({ ...s, spilloverPriority: v })),
    [],
  );
  const updateSenario = useCallback(
    (v: Senario) => setSettings((s) => ({ ...s, senario: v })),
    [],
  );
  const updateDisableGlobalOpt = useMemo(() => makeBoolUpdater('disableGlobalOpt'), [makeBoolUpdater]);

  const presenterProps = useMemo<PlanningSettingProps>(() => ({
    updateBalancedLoading,
    updateExcludeToll,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    updateActivateIncrementalMaxNumPistons,
    updateTraceCompanyId,
    updateSenario,
    updateSpilloverPriority,
    updateDisableGlobalOpt,
  }), [
    updateBalancedLoading, updateExcludeToll, updateIncludeFutureDeliveries, updateAutoSplitOrders,
    updateDontExchangePreroute, updateConcurrentAllOrNothing, updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes, updateExpandLoadStartMinutes, updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes, updateExpandUnloadEndMinutes, updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate, updateActivateIncrementalMaxNumPistons, updateTraceCompanyId,
    updateSenario, updateSpilloverPriority, updateDisableGlobalOpt,
  ]);

  return { settings, presenterProps };
}
